<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { setPromptVoice } from 'dashboard/api/relayVoiceManager';
import PromptSlot from './PromptSlot.vue';
import { SLOT_PATTERN, toSlotName, VOICES } from './helpers';

const props = defineProps({
  set: { type: Object, required: true },
  library: { type: Array, default: () => [] },
  tts: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['changed']);

const { t } = useI18n();
const { accountId } = useAccount();
const editingVoice = ref(props.set.voice);
const switching = ref(false);
const newSlot = ref('');
const draftSlots = ref([]);

watch(
  () => props.set.voice,
  voice => {
    editingVoice.value = voice;
  }
);

const key = computed(() => props.set.prompt_set.toUpperCase());
const otherVoice = computed(() =>
  props.set.voice === 'male' ? 'female' : 'male'
);
const missingOther = computed(() => props.set.missing[otherVoice.value] || []);
const slots = computed(() => [
  ...props.set.slots,
  ...draftSlots.value
    .filter(name => !props.set.slots.some(s => s.slot === name))
    .map(name => ({
      slot: name,
      routed: true,
      active: { male: null, female: null },
      can_revert: { male: false, female: false },
      versions: [],
    })),
]);
const voiceName = voice => t(`TEAM_LEAD.VOICES.${voice.toUpperCase()}`);

const switchVoice = async () => {
  switching.value = true;
  try {
    await setPromptVoice(
      accountId.value,
      props.set.prompt_set,
      otherVoice.value
    );
    useAlert(
      t('TEAM_LEAD.PROMPTS.VOICE_SWITCHED', {
        voice: voiceName(otherVoice.value),
      })
    );
    emit('changed');
  } catch (error) {
    useAlert(error.response?.data?.error || t('TEAM_LEAD.GENERIC_ERROR'));
  } finally {
    switching.value = false;
  }
};

const addSlot = () => {
  const name = toSlotName(newSlot.value);
  if (!SLOT_PATTERN.test(name)) {
    useAlert(t('TEAM_LEAD.PROMPTS.NEW_PROMPT_INVALID'));
    return;
  }
  if (!draftSlots.value.includes(name)) draftSlots.value.push(name);
  newSlot.value = '';
};
</script>

<template>
  <section
    class="rounded-xl outline outline-1 outline-n-container bg-n-solid-1 p-5 flex flex-col gap-4"
    :data-test-id="`prompt-set-${set.prompt_set}`"
  >
    <header class="flex items-start justify-between gap-4 flex-wrap">
      <div class="flex flex-col gap-1 max-w-xl">
        <h2 class="text-base font-semibold text-n-slate-12">
          {{ t(`TEAM_LEAD.PROMPTS.SETS.${key}.TITLE`) }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ t(`TEAM_LEAD.PROMPTS.SETS.${key}.DESCRIPTION`) }}
        </p>
      </div>
      <div class="flex flex-col items-end gap-2" data-tour="voice-switch">
        <span class="text-xs text-n-slate-11">
          {{
            t('TEAM_LEAD.PROMPTS.CALLERS_HEAR', { voice: voiceName(set.voice) })
          }}
        </span>
        <Button
          :label="
            t('TEAM_LEAD.PROMPTS.SWITCH_TO', { voice: voiceName(otherVoice) })
          "
          icon="i-lucide-repeat"
          size="sm"
          variant="faded"
          :disabled="missingOther.length > 0"
          :is-loading="switching"
          :data-test-id="`switch-${set.prompt_set}`"
          @click="switchVoice"
        />
      </div>
    </header>

    <p
      v-if="missingOther.length"
      class="rounded-lg bg-n-alpha-1 px-4 py-3 text-xs text-n-slate-11"
    >
      {{
        t('TEAM_LEAD.PROMPTS.SWITCH_BLOCKED', {
          voice: voiceName(otherVoice),
          n: missingOther.length,
        })
      }}
    </p>

    <div
      role="radiogroup"
      :aria-label="t('TEAM_LEAD.PROMPTS.EDITING_VOICE')"
      class="inline-flex w-fit rounded-lg bg-n-alpha-2 p-0.5"
    >
      <button
        v-for="voice in VOICES"
        :key="voice"
        type="button"
        role="radio"
        :aria-checked="editingVoice === voice"
        class="rounded-md px-3 py-1.5 text-sm font-medium transition-colors"
        :class="
          editingVoice === voice
            ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
            : 'text-n-slate-11 hover:text-n-slate-12'
        "
        @click="editingVoice = voice"
      >
        {{ t('TEAM_LEAD.PROMPTS.EDIT_VOICE', { voice: voiceName(voice) }) }}
        <span v-if="set.voice === voice" class="ms-1 text-xs text-n-teal-11">
          {{ t('TEAM_LEAD.PROMPTS.LIVE_SUFFIX') }}
        </span>
      </button>
    </div>

    <ul class="divide-y divide-n-weak border-t border-n-weak">
      <PromptSlot
        v-for="entry in slots"
        :key="`${entry.slot}-${editingVoice}`"
        :prompt-set="set.prompt_set"
        :entry="entry"
        :voice="editingVoice"
        :live-voice="set.voice"
        :library="library"
        :tts="tts"
        @changed="emit('changed')"
      />
    </ul>

    <form
      v-if="set.prompt_set === 'ivr'"
      class="flex items-end gap-2 flex-wrap border-t border-n-weak pt-4"
      data-tour="new-prompt"
      @submit.prevent="addSlot"
    >
      <div class="w-64">
        <Input
          v-model="newSlot"
          :label="t('TEAM_LEAD.PROMPTS.NEW_PROMPT')"
          :placeholder="t('TEAM_LEAD.PROMPTS.NEW_PROMPT_PLACEHOLDER')"
        />
      </div>
      <Button
        type="submit"
        :label="t('TEAM_LEAD.PROMPTS.ADD_PROMPT')"
        size="sm"
        variant="faded"
        :disabled="!newSlot.trim()"
      />
    </form>
  </section>
</template>
